import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
} from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined } from "@ant-design/icons";
import { propiedadesApi } from "../../services/propiedades.api";
import type {
  GeoCountry,
  GeoRegion,
  GeoComuna,
  Sector,
} from "../../services/propiedades.api";
import { paginacionCliente } from "../../utils/tablaPaginacion";
import { useFiltroPersistido } from "../../hooks/useFiltroPersistido";

/** Normaliza para búsqueda: minúsculas, sin tildes */
const strip = (s: string) =>
  s.trim().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

// ─── Generic section ────────────────────────────────────────────────────────

type GeoItem = { id: number; [k: string]: any };

interface SectionProps<T extends GeoItem> {
  title: string;
  nameField: string;
  nameLabel: string;
  parentField?: string;
  parentLabel?: string;
  parentOptions?: { value: number; label: string }[];
  items: T[];
  loading: boolean;
  /**
   * Clave con la que esta sección recuerda su buscador. La pone quien la usa:
   * el componente se instancia CUATRO veces en la misma pantalla (países,
   * regiones, comunas y sectores) y una clave inventada adentro las colapsaría
   * en un solo filtro compartido.
   */
  clavePersistencia: string;
  onRefresh: () => void;
  onCreate: (name: string, parentId?: number) => Promise<any>;
  onUpdate: (id: number, name: string, parentId?: number) => Promise<any>;
  onDelete: (id: number) => Promise<any>;
}

function GeoSection<T extends GeoItem>({
  title,
  nameField,
  nameLabel,
  parentField,
  parentLabel,
  parentOptions,
  items,
  loading,
  clavePersistencia,
  onRefresh,
  onCreate,
  onUpdate,
  onDelete,
}: SectionProps<T>) {
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<T | null>(null);
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useFiltroPersistido(`${clavePersistencia}.busqueda`, "", {
    validar: (v) => typeof v === "string",
  });

  const filteredItems = useMemo(() => {
    const needle = strip(search);
    if (!needle) return items;
    return items.filter((i) => strip(String(i[nameField] ?? "")).includes(needle));
  }, [items, search, nameField]);

  const parentMap = useMemo(() => {
    if (!parentOptions) return null;
    return new Map(parentOptions.map((o) => [o.value, o.label]));
  }, [parentOptions]);

  const handleSave = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      // `onCreate`/`onUpdate` van por `apiFetch`, que devuelve null en error
      // (p.ej. 409 por nombre duplicado) y ya muestra el toast con el motivo.
      // Solo cerramos/refrescamos si la operación tuvo éxito; ante un error
      // dejamos el modal abierto para que el admin corrija el nombre.
      const res = editing
        ? await onUpdate(editing.id, values[nameField], values[parentField!])
        : await onCreate(values[nameField], values[parentField!]);
      if (!res) return;
      setModalOpen(false);
      setEditing(null);
      form.resetFields();
      onRefresh();
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    await onDelete(id);
    onRefresh();
  };

  const columns: any[] = [
    {
      title: nameLabel,
      dataIndex: nameField,
      key: nameField,
      sorter: (a: T, b: T) => (a[nameField] ?? "").localeCompare(b[nameField] ?? ""),
    },
    ...(parentField && parentMap
      ? [
          {
            title: parentLabel,
            dataIndex: parentField,
            key: parentField,
            width: 200,
            render: (id: number) => parentMap.get(id) ?? `ID: ${id}`,
            sorter: (a: T, b: T) => (parentMap.get(a[parentField!]) ?? "").localeCompare(parentMap.get(b[parentField!]) ?? ""),
          },
        ]
      : []),
    {
      title: "Acciones",
      key: "acciones",
      width: 120,
      align: "center" as const,
      render: (_: any, record: T) => (
        <Space size="small">
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => {
              setEditing(record);
              form.setFieldsValue({
                [nameField]: record[nameField],
                ...(parentField && { [parentField]: record[parentField] }),
              });
              setModalOpen(true);
            }}
          />
          <Popconfirm
            title="¿Eliminar?"
            description="Se eliminará si no tiene dependencias."
            okText="Eliminar"
            cancelText="Cancelar"
            okButtonProps={{ danger: true }}
            onConfirm={() => handleDelete(record.id)}
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: 12,
        }}
      >
        <strong style={{ whiteSpace: "nowrap" }}>{title}</strong>
        <div style={{ flex: 1 }} />
        <Input
          placeholder={`Buscar ${title.toLowerCase()}...`}
          prefix={<SearchOutlined style={{ color: "#bbb" }} />}
          allowClear
          size="middle"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ maxWidth: 360 }}
        />
        <Button
          size="small"
          icon={<PlusOutlined />}
          onClick={() => {
            setEditing(null);
            form.resetFields();
            setModalOpen(true);
          }}
        >
          Agregar
        </Button>
      </div>
      <Table
        dataSource={filteredItems}
        columns={columns}
        rowKey="id"
        size="small"
        loading={loading}
        pagination={paginacionCliente({ porDefecto: 10, size: "small" })}
        scroll={{ x: "max-content" }}
        style={{ marginBottom: 32 }}
      />
      <Modal
        open={modalOpen}
        title={editing ? `Editar ${nameLabel.toLowerCase()}` : `Nuevo/a ${nameLabel.toLowerCase()}`}
        onOk={handleSave}
        confirmLoading={saving}
        onCancel={() => {
          setModalOpen(false);
          setEditing(null);
          form.resetFields();
        }}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name={nameField}
            label={nameLabel}
            rules={[{ required: true, message: "Requerido" }]}
          >
            <Input />
          </Form.Item>
          {parentField && parentOptions && (
            <Form.Item
              name={parentField}
              label={parentLabel}
              rules={[{ required: true, message: "Requerido" }]}
            >
              <Select
                showSearch
                optionFilterProp="label"
                placeholder={`Seleccionar ${parentLabel?.toLowerCase()}`}
                options={parentOptions}
              />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </>
  );
}

// ─── Main tab ───────────────────────────────────────────────────────────────

export default function GeoTab() {
  const [countries, setCountries] = useState<GeoCountry[]>([]);
  const [regions, setRegions] = useState<GeoRegion[]>([]);
  const [comunas, setComunas] = useState<GeoComuna[]>([]);
  const [sectores, setSectores] = useState<Sector[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    const [c, r, co, s] = await Promise.allSettled([
      propiedadesApi.getCountries(),
      propiedadesApi.getRegions(),
      propiedadesApi.getComunas(),
      propiedadesApi.getSectores(),
    ]);
    if (c.status === "fulfilled" && c.value?.data) setCountries(c.value.data);
    if (r.status === "fulfilled" && r.value?.data) setRegions(r.value.data);
    if (co.status === "fulfilled" && co.value?.data) setComunas(co.value.data);
    if (s.status === "fulfilled" && s.value?.data) setSectores(s.value.data);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const countryOpts = useMemo(
    () => countries.map((c) => ({ value: c.id, label: c.pais })),
    [countries],
  );
  const regionOpts = useMemo(
    () => regions.map((r) => ({ value: r.id, label: r.region })),
    [regions],
  );
  const comunaOpts = useMemo(
    () => comunas.map((c) => ({ value: c.id, label: c.comuna })),
    [comunas],
  );

  return (
    <div>
      <GeoSection<GeoCountry>
        title="Países"
        nameField="pais"
        nameLabel="País"
        items={countries}
        loading={loading}
        clavePersistencia="configuracion.zonasPaises"
        onRefresh={fetchAll}
        onCreate={(name) => propiedadesApi.createCountry(name)}
        onUpdate={(id, name) => propiedadesApi.updateCountry(id, name)}
        onDelete={(id) => propiedadesApi.deleteCountry(id)}
      />

      <GeoSection<GeoRegion>
        title="Regiones"
        nameField="region"
        nameLabel="Región"
        parentField="pais"
        parentLabel="País"
        parentOptions={countryOpts}
        items={regions}
        loading={loading}
        clavePersistencia="configuracion.zonasRegiones"
        onRefresh={fetchAll}
        onCreate={(name, parentId) => propiedadesApi.createRegion(name, parentId!)}
        onUpdate={(id, name, parentId) => propiedadesApi.updateRegion(id, name, parentId)}
        onDelete={(id) => propiedadesApi.deleteRegion(id)}
      />

      <GeoSection<GeoComuna>
        title="Comunas"
        nameField="comuna"
        nameLabel="Comuna"
        parentField="region"
        parentLabel="Región"
        parentOptions={regionOpts}
        items={comunas}
        loading={loading}
        clavePersistencia="configuracion.zonasComunas"
        onRefresh={fetchAll}
        onCreate={(name, parentId) => propiedadesApi.createComuna(name, parentId!)}
        onUpdate={(id, name, parentId) => propiedadesApi.updateComuna(id, name, parentId)}
        onDelete={(id) => propiedadesApi.deleteComuna(id)}
      />

      <GeoSection<Sector>
        title="Sectores"
        nameField="sector"
        nameLabel="Sector"
        parentField="comuna"
        parentLabel="Comuna"
        parentOptions={comunaOpts}
        items={sectores}
        loading={loading}
        clavePersistencia="configuracion.zonasSectores"
        onRefresh={fetchAll}
        onCreate={(name, parentId) => propiedadesApi.createSector(name, parentId!)}
        onUpdate={(id, name, parentId) => propiedadesApi.updateSector(id, name, parentId)}
        onDelete={(id) => propiedadesApi.deleteSector(id)}
      />
    </div>
  );
}
